import React, { useEffect, useState } from "react";
import logo from "assets/img/logo.png";
import axios from "axios";
import Cookies from "js-cookie";
import { useToast } from "@chakra-ui/react";
import config from "config";
import { Link as RouterLink, Redirect, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { translateBackendError } from "utils/errorUtils";
import packageJson from "../../../package.json";
import Loading from "misc/Loading";
import GoogleSignInButton from "components/GoogleSignInButton";

// Chakra imports
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  Switch,
  Text,
  DarkMode,
  keyframes,
} from "@chakra-ui/react";
import MufynButton from "components/Button";

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(3deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(34, 197, 94, 0.3), 0 0 40px rgba(34, 197, 94, 0.1); }
  50% { box-shadow: 0 0 40px rgba(34, 197, 94, 0.5), 0 0 80px rgba(34, 197, 94, 0.2); }
`;

const slideUp = keyframes`
  0% { opacity: 0; transform: translateY(30px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// Delay before redirect to ensure loading animation is visible
const REDIRECT_DELAY_MS = 100;

function SignIn() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const location = useLocation();

  // Support redirect after login (e.g. from pricing page)
  const searchParams = new URLSearchParams(location.search);
  const redirectFromUrl = searchParams.get("redirect");

  // Recover checkout intent stored during signup (survives email verification)
  // Try sessionStorage first (same tab), then localStorage (cross-tab / email verification in new tab)
  const storedRedirect = (() => {
    try {
      return sessionStorage.getItem("mufyn_post_login_redirect")
        || localStorage.getItem("mufyn_post_login_redirect")
        || null;
    } catch { return null; }
  })();
  const redirectAfterLogin = redirectFromUrl || storedRedirect;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  // Utiliser la langue du navigateur pour les pages publiques (signin)
  useEffect(() => {
    const getBrowserLanguage = () => {
      const browserLang = navigator.language || navigator.userLanguage;
      const langCode = browserLang.split('-')[0];
      return ['en', 'fr'].includes(langCode) ? langCode : 'en';
    };
    
    // Appliquer la langue du navigateur seulement si aucune langue n'est définie
    if (!i18n.language || i18n.language === 'en') {
      i18n.changeLanguage(getBrowserLanguage());
    }
  }, [i18n]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  useEffect(() => {
    const checkToken = async () => {
      try {
        if (!Cookies.get("loggedIn")) return;
        // HttpOnly cookie sent automatically
        const response = await axios.get(`${config.apiUrl}/checkToken`);
        if (response.status === 200) {
          setIsAuthenticated(true);
          try {
            sessionStorage.removeItem("mufyn_post_login_redirect");
            localStorage.removeItem("mufyn_post_login_redirect");
          } catch (e) {}
          window.location.href = redirectAfterLogin ? `/#${redirectAfterLogin}` : "/";
        }
      } catch (error) {}
    };
    checkToken();
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!email || !password) {
      toast({
        title: t("login.error.title"),
        description: t("login.error.fill_fields"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }
    
    setIsLoading(true);
    try {
      const token_expiration = rememberMe ? 365 : 7;
      const response = await axios.post(`${config.apiUrl}/login`, {
        email,
        password,
        token_expiration,
      });
      const { token } = response.data;
      // Store token for cross-origin Authorization header fallback
      Cookies.set("token", token, {
        expires: token_expiration,
        sameSite: "lax",
        secure: !config.isDev,
      });
      // Non-sensitive flag for UI state checks
      Cookies.set("loggedIn", "1", {
        expires: token_expiration,
        sameSite: "lax",
        secure: !config.isDev,
      });
      toast({
        title: t("login.success.title"),
        description: t("login.success.description", { appName: config.appName }),
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      // Show loading animation immediately for smooth transition
      setIsRedirecting(true);
      setIsAuthenticated(true);
      // Clear stored redirect intent
      try {
        sessionStorage.removeItem("mufyn_post_login_redirect");
        localStorage.removeItem("mufyn_post_login_redirect");
      } catch (e) {}
      // Small delay to ensure loading animation is visible before redirect
      setTimeout(() => {
        window.location.href = redirectAfterLogin ? `/#${redirectAfterLogin}` : "/";
      }, REDIRECT_DELAY_MS);
    } catch (error) {
      const errorCode = error.response?.data?.error;
      const retryMinutes = error.response?.data?.retryAfterMinutes;
      let message = error.response?.data?.message
        ? translateBackendError(error.response.data.message, t, "login.error.invalid_credentials")
        : t("login.error.invalid_credentials");
      let duration = 5000;
      let status = "error";
      
      // Rate limiting / Brute force protection messages
      if (
        errorCode === "TOO_MANY_REQUESTS" ||
        errorCode === "TOO_MANY_REQUESTS_BAN" ||
        errorCode === "BRUTE_FORCE_PROTECTION"
      ) {
        status = "warning";
        duration = 10000;
        if (errorCode === "BRUTE_FORCE_PROTECTION") {
          message = t("login.error.brute_force", { minutes: retryMinutes || 15 });
        } else if (errorCode === "TOO_MANY_REQUESTS_BAN") {
          message = t("login.error.ip_banned", { minutes: retryMinutes || 15 });
        } else {
          message = retryMinutes
            ? t("login.error.too_many_attempts", { minutes: retryMinutes })
            : t("login.error.too_many_attempts_generic");
        }
      } else if (error.response?.status === 429) {
        message = retryMinutes
          ? t("login.error.too_many_attempts", { minutes: retryMinutes })
          : t("login.error.too_many_attempts_generic");
        status = "warning";
        duration = 10000;
      } else if (error.response?.status === 403 && error.response?.data?.bannedUntil) {
        message = t("login.error.ip_banned", { minutes: retryMinutes || 15 });
        status = "error";
        duration = 15000;
      }
      
      toast({
        title: status === "warning" ? t("login.error.please_wait") : t("login.error.title"),
        description: error.code === "ERR_NETWORK"
          ? t("login.error.server_connection")
          : message,
        status: status,
        duration: duration,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credential) => {
    setIsGoogleLoading(true);
    try {
      const token_expiration = rememberMe ? 365 : 7;
      const browserLang = navigator.language || navigator.userLanguage;
      const langCode = browserLang.split('-')[0];
      const language = ['en', 'fr'].includes(langCode) ? langCode : 'en';

      const response = await axios.post(`${config.apiUrl}/google-auth`, {
        credential,
        token_expiration,
        language,
        locale: browserLang,
      });
      const { token, isNewUser } = response.data;
      Cookies.set("token", token, {
        expires: token_expiration,
        sameSite: "lax",
        secure: !config.isDev,
      });
      Cookies.set("loggedIn", "1", {
        expires: token_expiration,
        sameSite: "lax",
        secure: !config.isDev,
      });
      toast({
        title: t("login.success.title"),
        description: t("login.success.description", { appName: config.appName }),
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      setIsRedirecting(true);
      setIsAuthenticated(true);

      // For new Google users, redirect to onboarding
      if (isNewUser) {
        if (redirectAfterLogin) {
          try {
            sessionStorage.setItem("mufyn_post_login_redirect", redirectAfterLogin);
            localStorage.setItem("mufyn_post_login_redirect", redirectAfterLogin);
          } catch (e) {}
        }
        setTimeout(() => {
          window.location.href = "/#/auth/onboarding";
        }, REDIRECT_DELAY_MS);
      } else {
        try {
          sessionStorage.removeItem("mufyn_post_login_redirect");
          localStorage.removeItem("mufyn_post_login_redirect");
        } catch (e) {}
        setTimeout(() => {
          window.location.href = redirectAfterLogin ? `/#${redirectAfterLogin}` : "/";
        }, REDIRECT_DELAY_MS);
      }
    } catch (error) {
      const message = error.response?.data?.message
        ? translateBackendError(error.response.data.message, t, "login.error.invalid_credentials")
        : t("login.error.google_failed", "Google sign-in failed. Please try again.");
      toast({
        title: t("login.error.title"),
        description: error.code === "ERR_NETWORK"
          ? t("login.error.server_connection")
          : message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  // Show loading animation during redirect for smooth transition
  if (isRedirecting) {
    return <Loading />;
  }

  if (isAuthenticated) {
    return <Redirect to="/admin" />;
  }

  return (
    <Box
      minH="100vh"
      w="100%"
      position="relative"
      overflow="hidden"
      bg="#0a0d14"
    >
      {/* Animated Background */}
      <Box position="absolute" top="0" left="0" right="0" bottom="0" zIndex="0">
        {/* Base gradient */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bgGradient="radial(ellipse at 30% 20%, rgba(34, 197, 94, 0.15) 0%, transparent 50%), radial(ellipse at 70% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 50%), linear(to-br, #0a0d14, #0f1720, #0a0d14)"
        />
        
        {/* Floating orbs */}
        <Box
          position="absolute"
          top="10%"
          left="5%"
          w="300px"
          h="300px"
          borderRadius="full"
          bg="radial-gradient(circle, rgba(34, 197, 94, 0.2) 0%, transparent 70%)"
          filter="blur(40px)"
          animation={`${float} 8s ease-in-out infinite`}
        />
        <Box
          position="absolute"
          bottom="20%"
          right="10%"
          w="400px"
          h="400px"
          borderRadius="full"
          bg="radial-gradient(circle, rgba(74, 222, 128, 0.15) 0%, transparent 70%)"
          filter="blur(60px)"
          animation={`${float} 10s ease-in-out infinite reverse`}
        />
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          w="600px"
          h="600px"
          borderRadius="full"
          bg="radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 60%)"
          filter="blur(80px)"
          animation={`${pulse} 6s ease-in-out infinite`}
        />
        
        {/* Grid pattern */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          opacity="0.03"
          backgroundImage={`
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `}
          backgroundSize="80px 80px"
        />

        {/* Animated lines */}
        <Box
          position="absolute"
          top="30%"
          left="0"
          right="0"
          h="1px"
          background="linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.3), transparent)"
          animation={`${shimmer} 3s linear infinite`}
          backgroundSize="200% 100%"
        />
        <Box
          position="absolute"
          top="70%"
          left="0"
          right="0"
          h="1px"
          background="linear-gradient(90deg, transparent, rgba(74, 222, 128, 0.2), transparent)"
          animation={`${shimmer} 4s linear infinite 1s`}
          backgroundSize="200% 100%"
        />
      </Box>

      {/* Main Content */}
      <Flex
        position="relative"
        zIndex="1"
        minH="100vh"
        align="center"
        justify="center"
        p={{ base: 4, md: 8 }}
      >
        <Box
          as="form"
          onSubmit={handleLogin}
          w="100%"
          maxW="480px"
          animation={`${slideUp} 0.8s ease-out`}
        >
          {/* Glass Card */}
          <Box
            position="relative"
            p={{ base: 8, md: 12 }}
            borderRadius="24px"
            bg="linear-gradient(135deg, rgba(15, 20, 30, 0.8) 0%, rgba(20, 30, 40, 0.6) 100%)"
            backdropFilter="blur(20px)"
            border="1px solid rgba(34, 197, 94, 0.15)"
            boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
            overflow="hidden"
            _before={{
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              h: "1px",
              background: "linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.5), transparent)",
            }}
          >
            {/* Logo & Header */}
            <Flex direction="column" align="center" mb={8}>
              <Box position="relative" p={4} mb={0}>
                {/* Glow behind logo */}
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  w="150px"
                  h="150px"
                  borderRadius="full"
                  bg="radial-gradient(circle, rgba(34, 197, 94, 0.4) 0%, rgba(34, 197, 94, 0.1) 50%, transparent 70%)"
                  filter="blur(20px)"
                  animation={`${pulse} 3s ease-in-out infinite`}
                />
                <img
                  src={logo}
                  width="120px"
                  alt="logo"
                  style={{ position: "relative", zIndex: 1 }}
                />
              </Box>

              <Heading
                color="white"
                fontSize={{ base: "28px", md: "36px" }}
                fontWeight="bold"
                bgGradient="linear(to-r, #fff, #4ade80, #fff)"
                bgClip="text"
                backgroundSize="200% auto"
                animation={`${gradientMove} 5s linear infinite`}
              >
                {config.appName}
              </Heading>

              <Text mt={3} color="gray.400" fontSize="md" textAlign="center">
                {t("login.subtitle")}
              </Text>
            </Flex>

            {/* Form Fields */}
            <Box>
              {/* Email */}
              <FormControl mb={6}>
                <FormLabel color="gray.300" fontSize="sm" fontWeight="medium" mb={2}>
                  {t("login.email")}
                </FormLabel>
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  autoComplete="email"
                  placeholder={t("login.your_email")}
                  size="lg"
                  bg="rgba(15, 20, 30, 0.6)"
                  border="1px solid rgba(255, 255, 255, 0.1)"
                  borderRadius="16px"
                  color="white"
                  h="56px"
                  fontSize="md"
                  _placeholder={{ color: "gray.500" }}
                  _hover={{
                    border: "1px solid rgba(34, 197, 94, 0.3)",
                    bg: "rgba(15, 20, 30, 0.8)",
                  }}
                  _focus={{
                    border: "1px solid rgba(34, 197, 94, 0.6)",
                    boxShadow: "0 0 0 3px rgba(34, 197, 94, 0.15), inset 0 0 20px rgba(34, 197, 94, 0.05)",
                    bg: "rgba(15, 20, 30, 0.8)",
                  }}
                  transition="all 0.3s ease"
                />
              </FormControl>

              {/* Password */}
              <FormControl mb={6}>
                <FormLabel color="gray.300" fontSize="sm" fontWeight="medium" mb={2}>
                  {t("login.password")}
                </FormLabel>
                <Input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  autoComplete="current-password"
                  placeholder={t("login.your_password")}
                  size="lg"
                  bg="rgba(15, 20, 30, 0.6)"
                  border="1px solid rgba(255, 255, 255, 0.1)"
                  borderRadius="16px"
                  color="white"
                  h="56px"
                  fontSize="md"
                  _placeholder={{ color: "gray.500" }}
                  _hover={{
                    border: "1px solid rgba(34, 197, 94, 0.3)",
                    bg: "rgba(15, 20, 30, 0.8)",
                  }}
                  _focus={{
                    border: "1px solid rgba(34, 197, 94, 0.6)",
                    boxShadow: "0 0 0 3px rgba(34, 197, 94, 0.15), inset 0 0 20px rgba(34, 197, 94, 0.05)",
                    bg: "rgba(15, 20, 30, 0.8)",
                  }}
                  transition="all 0.3s ease"
                />
              </FormControl>

              {/* Remember Me & Forgot Password */}
              <FormControl display="flex" alignItems="center" justifyContent="space-between" mb={8}>
                <Flex alignItems="center">
                  <DarkMode>
                    <Switch
                      id="remember"
                      colorScheme="green"
                      isChecked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      sx={{
                        "span.chakra-switch__track": {
                          bg: "rgba(255, 255, 255, 0.1)",
                          _checked: {
                            bg: "linear-gradient(135deg, #22c55e, #10b981)",
                          },
                        },
                      }}
                    />
                  </DarkMode>
                  <FormLabel
                    htmlFor="remember"
                    mb="0"
                    ml={3}
                    color="gray.400"
                    fontSize="sm"
                    cursor="pointer"
                  >
                    {t("login.remember_me")}
                  </FormLabel>
                </Flex>
                <Link
                  as={RouterLink}
                  to="/auth/forgot-password"
                  color="#4ade80"
                  fontSize="sm"
                  fontWeight="medium"
                  _hover={{
                    color: "#22c55e",
                    textDecoration: "none",
                  }}
                  transition="color 0.2s ease"
                >
                  {t("login.forgot_password")}
                </Link>
              </FormControl>

              {/* Submit Button */}
              <MufynButton
                type="submit"
                size="xl"
                variant="primary"
                fullWidth
                isLoading={isLoading}
                loadingText={t("login.signing_in", "Signing in...")}
                withRipple
              >
                {t("login.button")}
              </MufynButton>

              {/* Divider */}
              <Flex align="center" my={6}>
                <Box flex="1" h="1px" bg="rgba(255, 255, 255, 0.1)" />
                <Text px={4} color="gray.500" fontSize="sm">
                  {t("login.or", "or")}
                </Text>
                <Box flex="1" h="1px" bg="rgba(255, 255, 255, 0.1)" />
              </Flex>

              {/* Google Sign-In */}
              <GoogleSignInButton
                onSuccess={handleGoogleSuccess}
                isLoading={isGoogleLoading}
              />
            </Box>

            {/* Footer Link */}
            <Flex justify="center" mt={8}>
              <Text color="gray.500" fontSize="sm">
                {t("login.no_account")}{" "}
                <Link
                  as={RouterLink}
                  to="/auth/signup"
                  color="#4ade80"
                  fontWeight="semibold"
                  _hover={{
                    color: "#22c55e",
                    textDecoration: "none",
                  }}
                  transition="color 0.2s ease"
                >
                  {t("login.register")}
                </Link>
              </Text>
            </Flex>

            {/* Back to landing - mobile only */}
            <Flex justify="center" mt={3} display={{ base: "flex", md: "none" }}>
              <Link
                as={RouterLink}
                to="/auth/landing"
                color="gray.500"
                fontSize="sm"
                _hover={{
                  color: "gray.300",
                  textDecoration: "none",
                }}
                transition="color 0.2s ease"
              >
                ← {t("login.back_to_home")}
              </Link>
            </Flex>

            {/* Version */}
            <Flex justify="center" mt={1}>
              <Text color="gray.500" fontSize="xs" fontFamily="monospace">
                v{packageJson.version}
              </Text>
            </Flex>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}

export default SignIn;
